import { useEffect, useState } from "react";
import { operationalServices } from "@/services";
import type { GeographicUnit, GeographyUnitType } from "@/types/domain";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface GeographyFilterProps {
  value?: string;
  onChange: (geographicUnitId: string | undefined) => void;
}

const LEVEL_LABELS: { type: GeographyUnitType; label: string }[] = [
  { type: "DISTRICT", label: "District" },
  { type: "COUNTY", label: "County" },
  { type: "SUBCOUNTY", label: "Subcounty / Town Council / Division" },
  { type: "PARISH", label: "Parish / Ward" },
];

export function GeographyFilter({ value, onChange }: GeographyFilterProps) {
  const [districts, setDistricts] = useState<GeographicUnit[]>([]);
  const [counties, setCounties] = useState<GeographicUnit[]>([]);
  const [subcounties, setSubcounties] = useState<GeographicUnit[]>([]);
  const [parishes, setParishes] = useState<GeographicUnit[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [selectedCounty, setSelectedCounty] = useState<string>("");
  const [selectedSubcounty, setSelectedSubcounty] = useState<string>("");
  const [selectedParish, setSelectedParish] = useState<string>("");

  useEffect(() => {
    operationalServices.geography.listUnits(undefined, "DISTRICT").then(setDistricts).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDistrict) { setCounties([]); return; }
    operationalServices.geography.listUnits(selectedDistrict, "COUNTY").then(setCounties).catch(() => {});
    setSelectedCounty(""); setSubcounties([]); setSelectedSubcounty(""); setParishes([]);
  }, [selectedDistrict]);

  useEffect(() => {
    if (!selectedCounty) { setSubcounties([]); return; }
    operationalServices.geography.listUnits(selectedCounty, undefined).then((units) => {
      setSubcounties(units.filter((u) => u.unitType === "SUBCOUNTY" || u.unitType === "TOWN_COUNCIL"));
    }).catch(() => {});
    setSelectedSubcounty(""); setParishes([]);
  }, [selectedCounty]);

  useEffect(() => {
    if (!selectedSubcounty) { setParishes([]); return; }
    operationalServices.geography.listUnits(selectedSubcounty, undefined).then((units) => {
      setParishes(units.filter((u) => u.unitType === "PARISH" || u.unitType === "WARD"));
    }).catch(() => {});
    setSelectedParish("");
  }, [selectedSubcounty]);

  useEffect(() => {
    if (selectedParish) onChange(selectedParish);
    else if (selectedSubcounty) onChange(selectedSubcounty);
    else if (selectedCounty) onChange(selectedCounty);
    else if (selectedDistrict) onChange(selectedDistrict);
    else onChange(undefined);
  }, [selectedDistrict, selectedCounty, selectedSubcounty, selectedParish, onChange]);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="geography-filter">
      {LEVEL_LABELS.map((level, idx) => {
        const options = [districts, counties, subcounties, parishes][idx];
        const selectedVal = [selectedDistrict, selectedCounty, selectedSubcounty, selectedParish][idx];
        const setter = [setSelectedDistrict, setSelectedCounty, setSelectedSubcounty, setSelectedParish][idx];
        const disabled = idx === 0 ? false : ![selectedDistrict, selectedCounty, selectedSubcounty][idx - 1];
        return (
          <div key={level.type} className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{level.label}</Label>
            <Select
              value={selectedVal}
              disabled={disabled || options.length === 0}
              onValueChange={(v: string) => { if (v === "__all__") setter(""); else setter(v); }}
            >
              <SelectTrigger className="h-9 text-sm" data-testid={`geo-filter-${level.type.toLowerCase()}`}>
                <SelectValue placeholder={disabled ? "Select above" : "All"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {options.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}
